import { cn } from '@/lib/utils'

/**
 * Skeleton — animated placeholder shape.
 * Figma: Design System / Compositions / Skeleton primitives.
 * Fill is `bg/subtle`; pulse animation respects prefers-reduced-motion.
 */
function Skeleton({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
    return (
        <div
            aria-hidden="true"
            className={cn(
                'animate-pulse rounded-md bg-[var(--bg-subtle)] motion-reduce:animate-none',
                className,
            )}
            {...props}
        />
    )
}

/**
 * ListPageSkeleton — full-page loading template for list/index pages.
 * Figma: Design System / Compositions / Skeleton — List page (242:915).
 *
 * Drop into a `loading.tsx` sibling of any list route to match the post-load
 * shape: PageHeader (title + subtitle + primary action) → Stats (3-up) →
 * Toolbar (search + filters) → Table (header + rows).
 */
interface ListPageSkeletonProps {
    /** Show the 3-up Stats grid. Default true. */
    showStats?: boolean
    /** Number of skeleton table rows. Default 6. */
    rows?: number
    /** Number of cells per row. Default 5. */
    columns?: number
}

function ListPageSkeleton({
    showStats = true,
    rows = 6,
    columns = 5,
}: ListPageSkeletonProps) {
    return (
        <div
            aria-busy="true"
            aria-live="polite"
            aria-label="Loading"
            className="flex flex-col gap-6 p-6"
        >
            {/* PageHeader: title + subtitle + primary action */}
            <header className="flex items-start justify-between gap-4">
                <div className="flex flex-col gap-2">
                    <Skeleton className="h-8 w-64" />
                    <Skeleton className="h-4 w-80" />
                </div>
                <Skeleton className="h-10 w-32 rounded-lg" />
            </header>

            {/* Stats: 3-up grid */}
            {showStats && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {Array.from({ length: 3 }).map((_, i) => (
                        <div
                            key={i}
                            className="flex flex-col gap-3 rounded-xl border border-[var(--border-subtle)] bg-[var(--bg-surface)] p-5"
                        >
                            <Skeleton className="h-4 w-24" />
                            <Skeleton className="h-7 w-16" />
                        </div>
                    ))}
                </div>
            )}

            {/* Toolbar: search + filters */}
            <div className="flex flex-wrap items-center gap-3">
                <Skeleton className="h-10 w-72 rounded-lg" />
                <Skeleton className="h-10 w-24 rounded-lg" />
                <Skeleton className="h-10 w-24 rounded-lg" />
            </div>

            {/* Table: header + rows */}
            <div className="rounded-xl border border-[var(--border-subtle)] bg-[var(--bg-surface)] overflow-hidden">
                <div
                    className="grid items-center gap-5 px-5 py-3 bg-[var(--bg-subtle)] border-b border-[var(--border-subtle)]"
                    style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}
                >
                    {Array.from({ length: columns }).map((_, i) => (
                        <Skeleton key={i} className="h-3 w-16" />
                    ))}
                </div>
                {Array.from({ length: rows }).map((_, r) => (
                    <div
                        key={r}
                        className="grid items-center gap-5 px-5 py-4 border-b border-[var(--border-subtle)] last:border-b-0"
                        style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}
                    >
                        {Array.from({ length: columns }).map((_, c) => (
                            <Skeleton
                                key={c}
                                className={cn('h-4', c === 0 ? 'w-32' : 'w-24')}
                            />
                        ))}
                    </div>
                ))}
            </div>
        </div>
    )
}

export { Skeleton, ListPageSkeleton }
